import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ChevronRight, ChevronLeft, Anchor, Ship, Users, DollarSign, MapPin, Sparkles, Mail, CheckCircle, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const questions = [
  {
    id: 'interest',
    title: "What's your primary interest?",
    subtitle: 'This helps us tailor recommendations to your needs',
    icon: Anchor,
    options: [
      { value: 'charter', label: 'Charter', description: 'Rent a yacht for a memorable voyage' },
      { value: 'purchase', label: 'Purchase', description: 'Buy your own luxury vessel' },
      { value: 'both', label: 'Exploring Both', description: "I'm open to either option" },
    ],
  },
  {
    id: 'type',
    title: 'What type of yacht appeals to you?',
    subtitle: 'Each offers a unique experience on the water',
    icon: Ship,
    options: [
      { value: 'motor', label: 'Motor Yacht', description: 'Speed, luxury, and spacious interiors' },
      { value: 'sailing', label: 'Sailing Yacht', description: 'Traditional elegance, eco-friendly' },
      { value: 'catamaran', label: 'Catamaran', description: 'Stability, space, shallow draft' },
      { value: 'any', label: 'No Preference', description: 'Show me all options' },
    ],
  },
  {
    id: 'guests',
    title: 'How many guests will typically be onboard?',
    subtitle: 'We\'ll find yachts with the right capacity',
    icon: Users,
    options: [
      { value: '4', label: '1-4 Guests', description: 'Intimate cruising experience' },
      { value: '8', label: '5-8 Guests', description: 'Perfect for families or small groups' },
      { value: '12', label: '9-12 Guests', description: 'Ideal for larger gatherings' },
      { value: '20', label: '12+ Guests', description: 'Corporate events or celebrations' },
    ],
  },
  {
    id: 'budget',
    title: 'What\'s your budget range?',
    subtitle: 'For charter (per week) or purchase price',
    icon: DollarSign,
    options: [
      { value: 'budget', label: 'Under $50K', description: 'Charter: ~$30-50K/week • Purchase: Up to $500K' },
      { value: 'mid', label: '$50K - $150K', description: 'Charter: ~$50-100K/week • Purchase: $500K-2M' },
      { value: 'luxury', label: '$150K - $500K', description: 'Charter: ~$100-200K/week • Purchase: $2M-5M' },
      { value: 'ultra', label: '$500K+', description: 'Charter: $200K+/week • Purchase: $5M+' },
    ],
  },
  {
    id: 'destination',
    title: 'Where would you like to cruise?',
    subtitle: 'Southeast Asia\'s most stunning destinations',
    icon: MapPin,
    options: [
      { value: 'Thailand', label: 'Thailand', description: 'Phuket, Phi Phi, Andaman Sea' },
      { value: 'Indonesia', label: 'Indonesia', description: 'Raja Ampat, Komodo, Bali' },
      { value: 'Malaysia', label: 'Malaysia', description: 'Langkawi, Tioman, Borneo' },
      { value: 'any', label: 'Anywhere in SE Asia', description: 'Show me all destinations' },
    ],
  },
];

export default function YachtFinder() {
  usePageTitle('Yacht Finder Quiz | Find Your Perfect Yacht');
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [emailForm, setEmailForm] = useState({ email: '', name: '', phone: '' });
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  const currentQuestion = questions[currentStep];
  const isLastQuestion = currentStep === questions.length - 1;
  const progress = ((currentStep + 1) / questions.length) * 100;

  const handleSelect = (value) => {
    setAnswers({ ...answers, [currentQuestion.id]: value });
  };

  const handleNext = async () => {
    if (isLastQuestion) {
      await findYachts();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const findYachts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/yachts`);
      const allYachts = response.data;
      
      // Filter yachts based on answers
      let filtered = allYachts.filter(yacht => {
        // Type filter
        if (answers.type && answers.type !== 'any') {
          const yachtType = yacht.yacht_type?.toLowerCase() || '';
          if (answers.type === 'motor' && !yachtType.includes('motor') && !yachtType.includes('power')) return false;
          if (answers.type === 'sailing' && !yachtType.includes('sail')) return false;
          if (answers.type === 'catamaran' && !yachtType.includes('catamaran')) return false;
        }
        
        // Guest capacity filter
        if (answers.guests) {
          const minGuests = parseInt(answers.guests);
          if (yacht.guests < minGuests * 0.5) return false;
        }
        
        // Destination filter
        if (answers.destination && answers.destination !== 'any') {
          if (!yacht.location?.toLowerCase().includes(answers.destination.toLowerCase())) return false;
        }
        
        // Budget filter (simplified)
        if (answers.budget && answers.interest !== 'purchase') {
          const weeklyRate = yacht.charter_weekly_usd || 0;
          if (answers.budget === 'budget' && weeklyRate > 50000) return false;
          if (answers.budget === 'mid' && (weeklyRate < 30000 || weeklyRate > 150000)) return false;
          if (answers.budget === 'luxury' && (weeklyRate < 80000 || weeklyRate > 500000)) return false;
        }
        
        if (answers.budget && answers.interest === 'purchase') {
          const price = yacht.price_usd || 0;
          if (answers.budget === 'budget' && price > 500000) return false;
          if (answers.budget === 'mid' && (price < 300000 || price > 2000000)) return false;
          if (answers.budget === 'luxury' && (price < 1500000 || price > 5000000)) return false;
        }
        
        return true;
      });
      
      // If no exact matches, show top yachts
      if (filtered.length === 0) {
        filtered = allYachts.slice(0, 3);
      }
      
      // Limit to top 6 results
      setResults(filtered.slice(0, 6));
      setShowEmailCapture(true); // Show email capture on results
    } catch (error) {
      console.error('Error fetching yachts:', error);
      setResults([]);
    }
    setLoading(false);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!emailForm.email) return;
    
    setEmailLoading(true);
    try {
      await axios.post(`${API_URL}/api/quiz-leads`, {
        email: emailForm.email,
        name: emailForm.name || null,
        phone: emailForm.phone || null,
        preferences: answers,
        recommended_yachts: results?.map(y => y.yacht_id) || []
      });
      setEmailSubmitted(true);
      setShowEmailCapture(false);
    } catch (error) {
      console.error('Error submitting lead:', error);
    }
    setEmailLoading(false);
  };

  const restartQuiz = () => {
    setCurrentStep(0);
    setAnswers({});
    setResults(null);
    setShowEmailCapture(false);
    setEmailSubmitted(false);
    setEmailForm({ email: '', name: '', phone: '' });
  };

  // Results view
  if (results !== null) {
    return (
      <div className="min-h-screen pt-32 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto pb-24">
        {/* Email Capture Banner */}
        {showEmailCapture && !emailSubmitted && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="email-capture-modal">
            <div className="glass-heavy max-w-lg w-full p-8 relative">
              <button 
                onClick={() => setShowEmailCapture(false)}
                className="absolute top-4 right-4 text-white/60 hover:text-white"
                data-testid="close-email-modal"
              >
                <X size={24} />
              </button>
              
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail size={32} className="text-gold" />
                </div>
                <h3 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Get Your Personalized Report
                </h3>
                <p className="text-white/70 text-sm">
                  Receive your yacht recommendations + exclusive Southeast Asia cruising guide via email
                </p>
              </div>
              
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <Input
                  type="email"
                  placeholder="Your email address *"
                  value={emailForm.email}
                  onChange={(e) => setEmailForm({...emailForm, email: e.target.value})}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40"
                  required
                  data-testid="email-input"
                />
                <Input
                  type="text"
                  placeholder="Your name (optional)"
                  value={emailForm.name}
                  onChange={(e) => setEmailForm({...emailForm, name: e.target.value})}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40"
                  data-testid="name-input"
                />
                <Input
                  type="tel"
                  placeholder="Phone number (optional)"
                  value={emailForm.phone}
                  onChange={(e) => setEmailForm({...emailForm, phone: e.target.value})}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40"
                  data-testid="phone-input"
                />
                <Button 
                  type="submit" 
                  className="w-full bg-gold text-[#0A192F] hover:bg-[#E5C55D]"
                  disabled={emailLoading}
                  data-testid="submit-email-btn"
                >
                  {emailLoading ? 'Sending...' : 'Send My Recommendations'}
                </Button>
                <button 
                  type="button"
                  onClick={() => setShowEmailCapture(false)}
                  className="w-full text-white/60 hover:text-white text-sm py-2"
                >
                  Skip for now
                </button>
              </form>
              
              <p className="text-white/40 text-xs text-center mt-4">
                We respect your privacy. Unsubscribe anytime.
              </p>
            </div>
          </div>
        )}

        {/* Success Banner */}
        {emailSubmitted && (
          <div className="mb-8 glass-light p-6 flex items-center gap-4 border-l-4 border-green-500" data-testid="email-success">
            <CheckCircle className="text-green-500 shrink-0" size={24} />
            <div>
              <p className="text-white font-medium">Your recommendations are on the way!</p>
              <p className="text-white/60 text-sm">Check your inbox for your personalized yacht report and SE Asia cruising guide.</p>
            </div>
          </div>
        )}

        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Sparkles size={40} className="text-gold" />
          </div>
          <h1 className="text-4xl md:text-5xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Your <span className="text-gold">Perfect Matches</span>
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto">
            Based on your preferences, we've selected {results.length} yacht{results.length !== 1 ? 's' : ''} that match your criteria.
          </p>
        </div>

        {results.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-testid="quiz-results">
            {results.map((yacht) => (
              <Link
                key={yacht.yacht_id}
                to={`/yacht/${yacht.yacht_id}`}
                className="group glass-light overflow-hidden hover:bg-white/10 transition-colors"
                data-testid={`result-yacht-${yacht.yacht_id}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={yacht.images?.[0] || 'https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=800'}
                    alt={yacht.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-gold mb-2">{yacht.yacht_type}</p>
                  <h3 className="text-xl font-medium mb-2 group-hover:text-gold transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {yacht.name}
                  </h3>
                  <p className="text-white/60 text-sm mb-4">{yacht.location}</p>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-white/60">{yacht.length_ft} ft • {yacht.guests} guests</span>
                    <span className="text-gold font-semibold">
                      {yacht.charter_weekly_usd 
                        ? `$${yacht.charter_weekly_usd.toLocaleString()}/week`
                        : `$${yacht.price_usd?.toLocaleString()}`
                      }
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 glass-light mb-12">
            <p className="text-white/60 mb-4">No exact matches found for your criteria.</p>
            <p className="text-white/40 text-sm">Try adjusting your preferences or contact us for personalized assistance.</p>
          </div>
        )}

        {/* Email CTA if not submitted */}
        {!emailSubmitted && (
          <div className="glass-heavy p-8 text-center mb-12" data-testid="email-cta-banner">
            <h3 className="text-2xl font-medium mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Want these sent to your inbox?
            </h3>
            <p className="text-white/70 mb-6 max-w-xl mx-auto">
              Get your personalized recommendations + our exclusive Southeast Asia Yacht Guide (PDF) delivered straight to your email.
            </p>
            <Button 
              onClick={() => setShowEmailCapture(true)}
              className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]"
              data-testid="open-email-modal-btn"
            >
              <Mail size={18} className="mr-2" /> Email My Results
            </Button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button onClick={restartQuiz} variant="outline" className="border-gold text-gold hover:bg-gold hover:text-[#0A192F]">
            <ChevronLeft size={18} className="mr-2" /> Start Over
          </Button>
          <Link to="/listings">
            <Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]">
              View All Yachts <ChevronRight size={18} className="ml-2" />
            </Button>
          </Link>
          <Link to="/contact">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
              Contact an Expert
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Quiz view
  const Icon = currentQuestion.icon;

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 lg:px-24 max-w-[900px] mx-auto pb-24">
      {/* Progress bar */}
      <div className="mb-12">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-white/40">Question {currentStep + 1} of {questions.length}</span>
          <span className="text-sm text-gold">{Math.round(progress)}% Complete</span>
        </div>
        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gold transition-all duration-500" 
            style={{ width: `${progress}%` }}
            data-testid="progress-bar"
          />
        </div>
      </div>

      {/* Question */}
      <div className="text-center mb-12">
        <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon size={32} className="text-gold" />
        </div>
        <h1 className="text-3xl md:text-4xl font-medium mb-3" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="question-title">
          {currentQuestion.title}
        </h1>
        <p className="text-white/60">{currentQuestion.subtitle}</p>
      </div>

      {/* Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12" data-testid="quiz-options">
        {currentQuestion.options.map((option) => {
          const isSelected = answers[currentQuestion.id] === option.value;
          return (
            <button
              key={option.value}
              onClick={() => handleSelect(option.value)}
              className={`p-6 text-left transition-all border-2 ${
                isSelected 
                  ? 'border-gold bg-gold/10' 
                  : 'border-white/10 hover:border-white/30 hover:bg-white/5'
              }`}
              data-testid={`option-${option.value}`}
            >
              <h3 className={`text-lg font-semibold mb-1 ${isSelected ? 'text-gold' : 'text-white'}`}>
                {option.label}
              </h3>
              <p className="text-sm text-white/60">{option.description}</p>
            </button>
          );
        })}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          onClick={handleBack}
          variant="ghost"
          className={`text-white/60 hover:text-white ${currentStep === 0 ? 'invisible' : ''}`}
          data-testid="back-btn"
        >
          <ChevronLeft size={18} className="mr-2" /> Back
        </Button>
        <Button
          onClick={handleNext}
          disabled={!answers[currentQuestion.id] || loading}
          className="bg-gold text-[#0A192F] hover:bg-[#E5C55D] disabled:opacity-50"
          data-testid="next-btn"
        >
          {loading ? (
            'Finding yachts...'
          ) : isLastQuestion ? (
            <>Find My Yacht <Sparkles size={18} className="ml-2" /></>
          ) : (
            <>Continue <ChevronRight size={18} className="ml-2" /></>
          )}
        </Button>
      </div>
    </div>
  );
}
